import moment from "moment";
import couriers from "@/constants/couriers.json";

export function showValue(value) {
  if (value === null || value === undefined ||
      (typeof value === "string" && !value.trim()) ||
      (Array.isArray(value) && value.length === 0)) return "-";
  return value;
}

export function formatoValor(row, column, value) {
  return showValue(value);
}

function formatEs(value, options) {
  if (showValue(value) === "-") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  // es-ES omits the thousands separator for four-digit numbers by default.
  return new Intl.NumberFormat("es-ES", options)
    .format(number)
    .replace(/^(-?\d)(\d{3})(?=,|$)/, "$1.$2");
}

export function formatNumero(value) {
  return formatEs(value);
}

// Importes en euros: siempre con dos decimales (1.234,50)
export function formatImporte(value) {
  return formatEs(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Sin ceros sobrantes por defecto; el mínimo permite presentaciones fijas (12,50).
export function formatDecimal(value, maxDecimales = 2, minDecimales = 0) {
  return formatEs(value, {
    minimumFractionDigits: minDecimales,
    maximumFractionDigits: maxDecimales,
  });
}

// Para la prop formatter de las columnas de el-table (fila, columna, valor):
// medidas y peso con 2 decimales como máximo (12,35)
export function formatoMedida(row, column, valor) {
  return formatDecimal(valor);
}

// Para la prop formatter de las columnas de el-table: importes en euros con
// dos decimales (1.234,50)
export function formatoImporte(row, column, valor) {
  return formatImporte(valor);
}

// Para la prop formatter de las columnas de el-table: precios unitarios con
// hasta 4 decimales, sin ceros sobrantes (0,035)
export function formatoPrecio(row, column, valor) {
  return formatDecimal(valor, 4);
}

export function formatHorario(value) {
  if (!value) return "";
  // Un horario antiguo guardado como objeto no debe romper la grid entera
  if (typeof value !== "string") return formatHorario(value.text);
  const franjaUnica = value.trim().match(/^desde\s+(\d{1,2}:\d{2}|-)\s+hasta\s+(\d{1,2}:\d{2}|-)$/i);
  if (franjaUnica) {
    return franjaUnica[1] === "-" && franjaUnica[2] === "-"
      ? ""
      : `${franjaUnica[1]} - ${franjaUnica[2]}`;
  }
  // List and detail responses already contain the morning/afternoon labels.
  const franjas = value.split(/(?:ma[ñn]ana|maniana|tarde)\s*:\s*/i);
  if (franjas.length === 1 || franjas[0].trim()) return value;

  const horarios = franjas.slice(1).map((franja) =>
    (franja.trim() || "- -").match(/^(\d{1,2}:\d{2}|-)\s+(\d{1,2}:\d{2}|-)$/)
  );
  if (horarios.some((horario) => !horario)) return value;

  return horarios
    .filter((horario) => horario[1] !== "-" || horario[2] !== "-")
    .map((horario) => `${horario[1]} - ${horario[2]}`)
    .join(" / ");
}

export function formatTipoParada(tipo) {
  return tipo === "ENTREGA" ? "Entrega" : tipo === "RECOGIDA" ? "Recogida" : "Recogida o entrega";
}

export function clearFiltersQuery(router, route, apply) {
  if (!Object.keys(route.query).length) {
    apply();
    return;
  }
  router.replace({ path: route.path, query: {}, hash: route.hash }, apply);
}

export function getFilterDateRange(value, fallback) {
  if (typeof value === "string") {
    const fecha = moment(value, "YYYY-MM-DD", true);
    if (fecha.isValid()) return [fecha.toDate(), fecha.toDate()];
  }
  if (Array.isArray(value) && value.length === 2 &&
      value.every((date) => date && Number.isFinite(new Date(date).getTime())) &&
      new Date(value[0]).getTime() <= new Date(value[1]).getTime()) {
    return value;
  }
  return fallback;
}

// Recuperar solo los campos conocidos y conservar siempre un rango utilizable.
export function recuperarFiltrosConFecha(storageKey, defaults, campoFecha = "date") {
  let saved = {};
  try {
    const value = JSON.parse(localStorage.getItem(storageKey));
    if (value && typeof value === "object" && !Array.isArray(value)) saved = value;
  } catch {
    // Un filtro ilegible no debe impedir abrir el listado con sus valores iniciales.
  }
  const result = { ...defaults };
  Object.keys(defaults).forEach((key) => {
    if (saved[key] != null) result[key] = saved[key];
  });
  const rango = getFilterDateRange(result[campoFecha], defaults[campoFecha]);
  result[campoFecha] = checkRangeFechas(rango, Infinity) ? rango : defaults[campoFecha];
  return result;
}

export function downloadFile(response) {
  const blob = response.blob instanceof Blob ? response.blob : new Blob([response.blob]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", response.fileName);
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }
}

export function formatAddress(address, countries = []) {
  if (!address) return "";
  const hasValue = (value) => showValue(value) !== "-";
  const street = [address.via, address.numero].filter(hasValue).join(", ");
  const streetPart = [street, address.extra].filter(hasValue).join(" ");
  const cityPart = [address.codigoPostal, address.poblacion].filter(hasValue).join(" ");
  const country = (countries || []).find((item) => item.iso2 === address.pais);
  const provinceCountryPart = [address.provincia, country ? country.nombre.es : address.pais]
    .filter(hasValue).join(", ");
  return [streetPart, cityPart, provinceCountryPart].filter(hasValue).join(", ");
}

export function checkRangeFechas(value, maxDaysRange) {
  if (!Array.isArray(value) || value.length !== 2 ||
      value.some((fecha) => fecha === null || fecha === undefined || fecha === "")) return false;
  const fechaInicio = moment(value[0]);
  const fechaFinal = moment(value[1]);
  if (!fechaInicio.isValid() || !fechaFinal.isValid() || fechaFinal.isBefore(fechaInicio)) return false;
  // El cambio de hora no añade ni quita días al rango del calendario.
  const dias = fechaFinal.startOf("day").diff(fechaInicio.startOf("day"), "days");
  return dias <= maxDaysRange;
}

// Backend timestamps are UTC, including those without an explicit Z suffix.
export function formatUtcDateLocal(fecha, format = "DD/MMM/YYYY HH:mm") {
  if (!fecha) return "";
  const local = moment.utc(fecha).local();
  return local.isValid() ? local.format(format) : "";
}

export function formatCourier(courier) {
  const item = couriers.find((item) => item.value === courier);
  return item ? item.name : courier || "-";
}

// A stop's date is a calendar day in UTC; state timestamps use local time.
export function formatUtcDate(fecha) {
  if (!fecha) return "-";
  const date = moment.utc(fecha);
  return date.isValid() ? date.format("DD/MMM/YYYY") : "-";
}


// Same classification as the backend's Paradas views. Partner takes precedence.
export function getVistaParada(parada) {
  if (!parada) return null;
  if (parada.tipo === "RECOGIDA") {
    if (parada.paradaFranquicia === true) return "recogidas_partner";
    if (parada.courier === "OXPERTA") return "recogidas_propias";
    if (parada.courier) return "recogidas_otras_plazas";
  }
  if (parada.tipo === "ENTREGA" && parada.courier === "OXPERTA") return "entregas_propias";
  return null;
}

export function getUrlParada(router, parada, vistasParadas) {
  const vista = getVistaParada(parada);
  const config = Object.values(vistasParadas).find((item) => item.vista === vista);
  // Sin nuestro número (enlace desde un envío de partner) se busca por la referencia del courier
  const porReferencia = vista === "recogidas_partner" ||
    (vista === "recogidas_otras_plazas" && !(parada && parada.numero));
  const campo = porReferencia ? "referenciaCourier" : "numero";
  const value = parada && parada[campo];
  const valor = value === null || value === undefined ? "" : String(value).trim();
  if (!config || !valor) return "";
  const query = { [campo]: valor, ignoreSavedFilters: "true" };
  const fecha = parada.fecha && moment.utc(parada.fecha);
  if (fecha && fecha.isValid()) query.fecha = fecha.format("YYYY-MM-DD");
  return router.resolve({ path: config.ruta, query }).href;
}

export function getUrlEnvio(router, identificador, fechaEnvio) {
  if (!identificador) return "";
  const query = { identificadores: identificador, ignoreSavedFilters: "true" };
  const fecha = fechaEnvio && moment.utc(fechaEnvio);
  if (fecha && fecha.isValid()) query.fecha = fecha.format("YYYY-MM-DD");
  return router.resolve({ path: "/franquicia/envios", query }).href;
}
